"use client";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Filter, Plus, Edit3, Trash2, Grid3X3, ChevronDown, Tag, Table, Layers, CheckCircle } from 'lucide-react';
import SubCategoryModal from './SubCategoryModal';
import { useSubCategories } from '../context/SubcategoryContext';
import { useCategories } from '../context/CategoryContext';

const SubCategoryPage = () => {
  const { subCategories, loading, fetchSubCategories, addSubCategory, updateSubCategory, deleteSubCategory, toggleStatus } = useSubCategories();
  const { categories, fetchCategories } = useCategories();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  /* ── Toast State ── */
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = useCallback((message = 'Status updated successfully!', type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  useEffect(() => {
    fetchSubCategories().catch(err => console.warn("Failed to fetch sub-categories", err));
    fetchCategories().catch(err => console.warn("Failed to fetch categories", err));
  }, [fetchSubCategories, fetchCategories]);

  const categoryStatusMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => { map[cat.id] = cat.status; });
    return map;
  }, [categories]);

  const getEffectiveStatus = (subCategory) =>
    categoryStatusMap[subCategory.category_id] === 'Active'
      ? subCategory.status
      : 'Inactive';

  const isCategoryInactive = (subCategory) =>
    categoryStatusMap[subCategory.category_id] !== 'Active';

  const statusOptions = useMemo(() => [
    { value: 'all',      label: 'All Sub-Categories', count: subCategories.length },
    { value: 'Active',   label: 'Active',   count: subCategories.filter(c => getEffectiveStatus(c) === 'Active').length },
    { value: 'Inactive', label: 'Inactive', count: subCategories.filter(c => getEffectiveStatus(c) === 'Inactive').length },
  ], [subCategories, categoryStatusMap]);

  const categoryOptions = useMemo(() => {
    const statusFiltered = statusFilter === 'all'
      ? subCategories
      : subCategories.filter(sub => getEffectiveStatus(sub) === statusFilter);

    const filtered = categories
      .map(cat => ({
        value: cat.id,
        label: cat.name,
        count: statusFiltered.filter(sub => String(sub.category_id) === String(cat.id)).length,
      }))
      .filter(opt => opt.count > 0);

    return [
      { value: 'all', label: 'All Categories', count: statusFiltered.length },
      ...filtered,
    ];
  }, [categories, subCategories, statusFilter, categoryStatusMap]);

  useEffect(() => {
    if (categoryFilter === 'all') return;
    const stillValid = categoryOptions.some(opt => String(opt.value) === String(categoryFilter));
    if (!stillValid) setCategoryFilter('all');
  }, [categoryOptions, categoryFilter]);

  const sortOptions = [
    { value: 'name',      label: 'Name A-Z' },
    { value: 'name-desc', label: 'Name Z-A' },
    { value: 'category',  label: 'Category' },
    { value: 'newest',    label: 'Newest First' },
    { value: 'oldest',    label: 'Oldest First' },
  ];

  const filteredAndSortedSubCategories = useMemo(() => {
    let filtered = subCategories.filter((subCategory) => {
      const matchesSearch =
        searchTerm === '' ||
        subCategory.subcat_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subCategory.category_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const effectiveStatus = getEffectiveStatus(subCategory);
      const matchesStatus = statusFilter === 'all' || effectiveStatus === statusFilter;
      const matchesCategory =
        categoryFilter === 'all' || String(subCategory.category_id) === String(categoryFilter);

      return matchesSearch && matchesStatus && matchesCategory;
    });

    const parseDate = (value) => {
      if (!value) return 0;
      const normalized = value.replace(' ', 'T');
      const time = new Date(normalized).getTime();
      return isNaN(time) ? 0 : time;
    };

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':      return (a.subcat_name || '').localeCompare(b.subcat_name || '');
        case 'name-desc': return (b.subcat_name || '').localeCompare(a.subcat_name || '');
        case 'category':  return (a.category_name || '').localeCompare(b.category_name || '');
        case 'newest':    return parseDate(b.create_audit_time) - parseDate(a.create_audit_time);
        case 'oldest':    return parseDate(a.create_audit_time) - parseDate(b.create_audit_time);
        default:          return 0;
      }
    });

    return filtered;
  }, [subCategories, searchTerm, statusFilter, categoryFilter, sortBy, categoryStatusMap]);

  // ── HANDLERS ────────────────────────────────────────────────────────────────
  const handleSelectAll = () => {
    if (selectedSubCategories.length === filteredAndSortedSubCategories.length) {
      setSelectedSubCategories([]);
    } else {
      setSelectedSubCategories(filteredAndSortedSubCategories.map(c => c.id));
    }
  };

  const handleDeleteSubCategory = async (subCategoryId) => {
    if (window.confirm('Are you sure you want to delete this sub-category?')) {
      try {
        await deleteSubCategory(subCategoryId);
        setSelectedSubCategories(prev => prev.filter(id => id !== subCategoryId));
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
    setActiveDropdown(null);
  };

  const handleEditSubCategory = (subCategory) => {
    setEditingSubCategory(subCategory);
    setShowAddModal(true);
    setActiveDropdown(null);
  };

  /* ── Toggle Status with Toast ── */
  const handleToggleStatus = async (subCategoryId, currentStatus) => {
    try {
      await toggleStatus(subCategoryId, currentStatus);
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      showToast(`Status updated to ${newStatus} successfully!`);
    } catch (err) {
      console.error('Toggle status failed', err);
      showToast('Failed to update status.', 'error');
    }
    setActiveDropdown(null);
  };

  const handleSaveSubCategory = async (subCategoryData) => {
    try {
      if (editingSubCategory) {
        await updateSubCategory(editingSubCategory.id, subCategoryData);
      } else {
        await addSubCategory(subCategoryData);
      }
      setEditingSubCategory(null);
      setShowAddModal(false);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleAddSubCategory = () => {
    setEditingSubCategory(null);
    setShowAddModal(true);
  };

  /* ── Toast Component ── */
  const Toast = () => (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
        pointerEvents: toast.visible ? 'auto' : 'none',
      }}
    >
      <div
        className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
        }`}
      >
        {toast.type === 'error' ? (
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
        ) : (
          <CheckCircle className="w-5 h-5 shrink-0" />
        )}
        {toast.message}
      </div>
    </div>
  );

  /* ── Reusable Toggle Switch ── */
  const ToggleSwitch = ({ subCategoryId, status, disabled = false }) => (
    <label
      className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      title={disabled ? 'Category is inactive' : status === 'Active' ? 'Set Inactive' : 'Set Active'}
    >
      <input
        type="checkbox"
        checked={status === 'Active'}
        onChange={() => !disabled && handleToggleStatus(subCategoryId, status)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-sm"></div>
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5"></div>
    </label>
  );

  /* ── Grid Card ── */
  const SubCategoryCard = ({ subCategory }) => {
    const catInactive = isCategoryInactive(subCategory);
    return (
      <div className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 transform group ${catInactive ? 'border-gray-200 opacity-60 grayscale' : 'border-teal-200 hover:shadow-2xl hover:shadow-teal-200/50 hover:-translate-y-1'}`}>
        <div className="relative w-full h-48">
          <img src={subCategory.image} alt={subCategory.subcat_name} className="w-full h-full object-cover" />
          <div className="absolute top-3 right-3">
            <ToggleSwitch subCategoryId={subCategory.id} status={subCategory.status} disabled={catInactive} />
          </div>
          {catInactive && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-0.5 bg-gray-700/80 text-white text-xs font-medium rounded-full">Category Inactive</span>
            </div>
          )}
        </div>
        <div className="p-4 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1">
              <Tag className="w-3 h-3 text-teal-500 shrink-0" />
              <span className="text-xs text-teal-500 font-medium truncate">{subCategory.category_name}</span>
            </div>
            <h3 className="text-base font-semibold text-teal-800 group-hover:text-teal-900 truncate">{subCategory.subcat_name}</h3>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{subCategory.description}</p>
          </div>
          <div className="flex flex-row gap-2 shrink-0 mt-14">
            <button
              onClick={() => handleEditSubCategory(subCategory)}
              className="flex items-center gap-1 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-xs font-medium"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDeleteSubCategory(subCategory.id)}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl flex items-start font-bold text-teal-800">Sub-Categories</h1>
              <p className="text-gray-600 mt-1">Manage your product sub-categories</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAddSubCategory}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >
              <Plus className="w-5 h-5" />
              Add Sub-Category
            </button>
          </div>
        </header>

        {/* Search and Filters */}
        <section className="bg-white bg-opacity-60 rounded-xl border border-teal-200 p-6 mb-8 hover:shadow-2xl hover:shadow-teal-200/50 transition-all duration-300">
          <div className="flex flex-col md:flex-row md:justify-between gap-3 mb-4">
            <div className="relative w-1/2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search sub-categories by name, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-lg p-2"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {categoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              {selectedSubCategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bulk Actions ({selectedSubCategories.length} selected)
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm"
                      onClick={async () => {
                        if (!window.confirm('Delete all selected sub-categories?')) return;
                        for (const id of selectedSubCategories) {
                          try { await deleteSubCategory(id); } catch (e) { console.error(e); }
                        }
                        setSelectedSubCategories([]);
                      }}
                    >
                      Delete Selected
                    </button>
                    <button
                      className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-sm"
                      onClick={async () => {
                        for (const id of selectedSubCategories) {
                          const sub = subCategories.find(s => s.id === id);
                          if (!sub) continue;
                          try { await toggleStatus(id, sub.status); } catch (e) { console.error(e); }
                        }
                        showToast('Bulk status updated successfully!');
                      }}
                    >
                      Toggle Status
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {loading && <div className="mb-4 text-sm text-gray-500">Loading sub-categories...</div>}

        {/* Sub-Categories Section */}
        <section>
          {filteredAndSortedSubCategories.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Tag className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">No sub-categories found</h2>
              <p className="text-gray-600 mb-6">
                {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create your first sub-category to get started.'}
              </p>
              <button
                onClick={handleAddSubCategory}
                className="bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium"
              >
                Add Sub-Category
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-gray-600">
                  Showing {filteredAndSortedSubCategories.length} of {subCategories.length} sub-categories
                </span>
                <div className="flex items-center gap-2">
                  {[
                    { mode: 'grid', Icon: Grid3X3 },
                    { mode: 'list', Icon: Layers },
                    { mode: 'table', Icon: Table },
                  ].map(({ mode, Icon }) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      className={`p-2 border rounded-lg transition-colors ${viewMode === mode ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <Icon size={18} />
                    </button>
                  ))}
                </div>
              </div>

              {/* ── GRID VIEW ── */}
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredAndSortedSubCategories.map((subCategory) => (
                    <SubCategoryCard key={subCategory.id} subCategory={subCategory} />
                  ))}
                </div>
              )}

              {/* ── LIST VIEW ── */}
              {viewMode === 'list' && (
                <div className="flex flex-col gap-3">
                  {filteredAndSortedSubCategories.map((subCategory) => (
                    <div
                      key={subCategory.id}
                      className={`bg-white border rounded-xl px-5 py-4 flex items-center gap-4 transition-all duration-200 ${isCategoryInactive(subCategory) ? 'border-gray-200 opacity-60 grayscale' : 'border-gray-200 hover:shadow-md hover:border-teal-200'}`}
                    >
                      <img
                        src={subCategory.image}
                        alt={subCategory.subcat_name}
                        className="w-14 h-14 rounded-lg object-cover border border-gray-100 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">{subCategory.subcat_name}</h3>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Tag className="w-3 h-3 text-teal-500 shrink-0" />
                          <span className="text-xs text-teal-500 truncate">{subCategory.category_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <ToggleSwitch subCategoryId={subCategory.id} status={subCategory.status} disabled={isCategoryInactive(subCategory)} />
                        <button onClick={() => handleEditSubCategory(subCategory)} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Edit">
                          <Edit3 size={16} />
                        </button>
                        <button onClick={() => handleDeleteSubCategory(subCategory.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── TABLE VIEW ── */}
              {viewMode === 'table' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Image</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sub-Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredAndSortedSubCategories.map((subCategory) => (
                        <tr
                          key={subCategory.id}
                          className={`transition-colors ${isCategoryInactive(subCategory) ? 'opacity-60 grayscale bg-gray-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-4 py-3">
                            <img src={subCategory.image} alt={subCategory.subcat_name} className="w-10 h-10 rounded-lg object-cover border border-gray-100" />
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800">{subCategory.subcat_name}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Tag className="w-3 h-3 text-teal-500 shrink-0" />
                              <span className="text-gray-600">{subCategory.category_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-3">
                              <ToggleSwitch subCategoryId={subCategory.id} status={subCategory.status} disabled={isCategoryInactive(subCategory)} />
                              <button onClick={() => handleEditSubCategory(subCategory)} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Edit">
                                <Edit3 size={16} />
                              </button>
                              <button onClick={() => handleDeleteSubCategory(subCategory.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Sub-Category Modal */}
      <SubCategoryModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingSubCategory(null);
        }}
        subCategory={editingSubCategory}
        categories={categories}
        onSave={handleSaveSubCategory}
      />

      {activeDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setActiveDropdown(null)} />
      )}

      {/* ── Toast Notification ── */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9999,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: toast.visible ? 1 : 0,
          transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
          pointerEvents: toast.visible ? 'auto' : 'none',
        }}
      >
        <div
          className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
          }`}
        >
          {toast.type === 'error' ? (
            <svg className="w-5 h-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          ) : (
            <CheckCircle className="w-5 h-5 shrink-0" />
          )}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default SubCategoryPage;